import React, { useMemo, useCallback } from 'react';
import { Box, Tooltip, IconButton, Chip, InputBase, Stack, Typography } from '@mui/material';
import { useGridApiContext } from '@mui/x-data-grid';
import {
  Delete as DeleteIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  HourglassEmpty as StagedIcon,
  Sync as CreatingIcon,
  Receipt as ChequeIcon,
} from '@mui/icons-material';
import { EasyCheckDataGridBasic } from '../../common/Controls';
import { formatDateForDisplay } from '../../../utils/dateUtils';
import { getAmountInputRegex, formatCurrencyAmount } from '../../../utils/currencyUtils';

const statusConfig = {
  Staged: { color: 'default', icon: <StagedIcon fontSize="small" />, label: 'Staged' },
  Creating: { color: 'info', icon: <CreatingIcon fontSize="small" sx={{ animation: 'spin 1s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />, label: 'Creating' },
  Created: { color: 'success', icon: <SuccessIcon fontSize="small" />, label: 'Created' },
  Error: { color: 'error', icon: <ErrorIcon fontSize="small" />, label: 'Error' },
};

const AmountEditCell = (params) => {
  const apiRef = useGridApiContext();
  const { id, field, value, currency = 'AED' } = params;

  const handleChange = useCallback((event) => {
    const newValue = event.target.value;
    if (newValue === '' || getAmountInputRegex(currency).test(newValue)) {
      // Reject amounts over the cap (matches single/recurring/mobile forms).
      if (newValue !== '' && parseFloat(newValue) > 9999999999.99) return;
      apiRef.current.setEditCellValue({ id, field, value: newValue === '' ? '' : newValue });
    }
  }, [apiRef, id, field, currency]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      apiRef.current.stopCellEditMode({ id, field });
      const rowIndex = apiRef.current.getRowIndexRelativeToVisibleRows(id);
      const allRows = apiRef.current.getAllRowIds();
      const targetIndex = event.key === 'ArrowUp' ? rowIndex - 1 : rowIndex + 1;
      if (targetIndex >= 0 && targetIndex < allRows.length) {
        apiRef.current.setCellFocus(allRows[targetIndex], field);
      }
    }
  }, [apiRef, id, field]);

  return (
    <InputBase
      value={value ?? ''}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      autoFocus
      fullWidth
      inputProps={{
        style: { textAlign: 'right', padding: '0 10px' },
        inputMode: 'decimal',
      }}
    />
  );
};

const BulkChequeGrid = ({
  rows,
  onProcessRowUpdate,
  onDeleteRow,
  loading,
  emptyMessage,
  emptySubMessage,
  rowSelectionModel,
  onRowSelectionModelChange,
  currency = 'AED',
}) => {
  const columns = useMemo(() => [
    {
      field: 'rowIndex',
      headerName: '#',
      width: 60,
      editable: false,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const index = params.api.getRowIndexRelativeToVisibleRows(params.id);
        return index + 1;
      },
    },
    {
      field: 'chequeNumber',
      headerName: 'Cheque #',
      description: 'Leave blank to auto-generate the next sequential number for this bank account',
      width: 130,
      editable: true,
      renderCell: (params) => (
        <Box sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
          {params.value || <Box component="span" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>auto</Box>}
        </Box>
      ),
    },
    {
      field: 'beneficiaryName',
      headerName: 'Beneficiary',
      flex: 1.5,
      minWidth: 180,
      editable: true,
    },
    {
      field: 'chqAmount',
      headerName: 'Amount',
      width: 130,
      editable: true,
      align: 'right',
      headerAlign: 'right',
      renderEditCell: (params) => <AmountEditCell {...params} currency={currency} />,
      valueFormatter: (value) => {
        if (value == null || value === 0) return '';
        return formatCurrencyAmount(value, currency, { withSymbol: false });
      },
      valueSetter: (value, row) => {
        return { ...row, chqAmount: value === '' ? 0 : parseFloat(value) || 0 };
      },
    },
    {
      field: 'chqDate',
      headerName: 'Cheque Date',
      width: 140,
      editable: true,
      type: 'date',
      valueGetter: (value) => {
        if (!value) return null;
        return new Date(value + 'T00:00:00');
      },
      valueSetter: (value, row) => {
        if (!value) return { ...row, chqDate: '' };
        const dateObj = value instanceof Date ? value : new Date(value);
        if (isNaN(dateObj.getTime())) return { ...row, chqDate: '' };
        return { ...row, chqDate: dateObj.toISOString().split('T')[0] };
      },
      renderCell: (params) => {
        if (!params.row.chqDate) return '-';
        return formatDateForDisplay(params.row.chqDate);
      },
    },
    {
      field: 'remarks',
      headerName: 'Remarks',
      flex: 1,
      minWidth: 120,
      editable: true,
    },
    {
      field: 'rowStatus',
      headerName: 'Status',
      width: 110,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        const config = statusConfig[params.value] || statusConfig.Staged;
        const chip = (
          <Chip
            icon={config.icon}
            label={config.label}
            color={config.color}
            size="small"
            variant="outlined"
          />
        );
        if (params.value === 'Error' && params.row.errorMessage) {
          return <Tooltip title={params.row.errorMessage} arrow>{chip}</Tooltip>;
        }
        return chip;
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      editable: false,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        if (params.row.rowStatus !== 'Staged') return null;
        return (
          <IconButton
            size="small"
            color="error"
            onClick={() => onDeleteRow(params.row.id)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        );
      },
    },
  ], [onDeleteRow, currency]);

  const getRowClassName = (params) => {
    if (params.row.rowStatus === 'Error') return 'row-error';
    if (params.row.rowStatus === 'Created') return 'row-created';
    return '';
  };

  const isCellEditable = (params) => {
    return params.row.rowStatus === 'Staged';
  };

  const handleProcessRowUpdate = (newRow, oldRow) => {
    return onProcessRowUpdate(newRow, oldRow);
  };

  return (
    <Box sx={{
      flexGrow: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      position: 'relative',
      border: '1px solid #e0e0e0',
      borderRadius: 1,
      backgroundColor: 'white',
      '& .row-error': { backgroundColor: 'rgba(211, 47, 47, 0.04)' },
      '& .row-created': { backgroundColor: 'rgba(46, 125, 50, 0.04)' },
    }}>
      <EasyCheckDataGridBasic
        rows={rows}
        columns={columns}
        loading={loading}
        editMode="cell"
        isCellEditable={isCellEditable}
        processRowUpdate={handleProcessRowUpdate}
        onProcessRowUpdateError={(error) => console.error('Row update error:', error)}
        getRowClassName={getRowClassName}
        checkboxSelection
        isRowSelectable={(params) => params.row.rowStatus === 'Created'}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={onRowSelectionModelChange}
        disableRowSelectionOnClick
        pageSize={100}
        rowsPerPageOptions={[25, 50, 100]}
        hideFooterSelectedRowCount
        rowHeight={64}
        columnHeaderHeight={56}
        disableVirtualization
        slots={{
          noRowsOverlay: () => (
            <Stack
              height="100%"
              alignItems="center"
              justifyContent="center"
              spacing={2}
              sx={{ py: 2 }}
            >
              <ChequeIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
              <Typography variant="h6" color="text.secondary">
                {emptyMessage || 'No cheques in staging grid'}
              </Typography>
              <Typography variant="body2" color="text.disabled">
                {emptySubMessage || 'Use the action buttons above to add cheques'}
              </Typography>
            </Stack>
          ),
        }}
        sx={{
          height: '100%',
          width: '100%',
          border: 'none',
          overflow: 'hidden',
          '& .MuiDataGrid-main': {
            overflow: 'hidden',
          },
          '& .MuiDataGrid-virtualScroller': {
            overflow: 'auto !important',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid #f5f5f5',
            borderRight: 'none !important',
            borderLeft: 'none !important',
            padding: '16px 12px',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#F7F9FC',
            borderBottom: '1px solid #e8e8e8 !important',
            borderTop: 'none !important',
            height: '56px',
            minHeight: '56px !important',
          },
          '& .MuiDataGrid-columnHeader': {
            borderRight: 'none !important',
            borderLeft: 'none !important',
            borderBottom: 'none !important',
            color: '#000000 !important',
            display: 'flex !important',
            alignItems: 'center !important',
            justifyContent: 'flex-start !important',
            height: '56px !important',
            fontWeight: '600 !important',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: '600 !important',
          },
          '& .MuiDataGrid-footerContainer': {
            minHeight: '52px',
            maxHeight: '52px',
          },
        }}
      />
    </Box>
  );
};

export default BulkChequeGrid;
